import { useState } from "react";

import { Modal, Tabs, type TabsItem } from "@virtual-brain-lib/frontend-design-system/ui";
import type {
	MessageToolAnalysisData,
	MessageToolAnalysisDocument,
} from "@virtual-brain-lib/types";

import { DocumentSectionHeader } from "../document-tool-card/DocumentSectionHeader";
import { AnalysisDocumentRow } from "./AnalysisDocumentRow";
import { groupAnalysisDocumentsBySourceLabel } from "./groupAnalysisDocumentsBySourceLabel";

type ViewMode = "default" | "grouped";

interface ToolCardAllAnalysisDocumentsModalProps {
	readonly isOpen: boolean;
	readonly close: () => void;
	readonly documents: MessageToolAnalysisData["documents"];
	readonly onDocumentClick?: (doc: MessageToolAnalysisDocument) => void;
}

export function ToolCardAllAnalysisDocumentsModal({
	isOpen,
	close,
	documents,
	onDocumentClick,
}: ToolCardAllAnalysisDocumentsModalProps) {
	const [viewMode, setViewMode] = useState<ViewMode>("default");
	const hasAnySourceLabel = documents.some((doc) => Boolean(doc.sourceLabel));

	const tabs: Array<TabsItem<unknown, ViewMode>> = [
		{ key: "default", title: "Relevance" },
		{ key: "grouped", title: "Source" },
	];

	const effectiveViewMode = hasAnySourceLabel ? viewMode : "default";

	return (
		<Modal
			close={close}
			icon="scanSearch"
			isOpen={isOpen}
			size="md"
			title={`All documents (${documents.length})`}
		>
			<div className="flex flex-col gap-2">
				{hasAnySourceLabel && (
					<Tabs
						selectedKey={viewMode}
						size="sm"
						tabs={tabs}
						variant="underline"
						onSelectedTabChange={(key) => {
							setViewMode(key);
						}}
					/>
				)}

				<div className="max-h-[60vh] overflow-y-auto">
					{effectiveViewMode === "default" ? (
						<DefaultView documents={documents} onDocumentClick={onDocumentClick} />
					) : (
						<GroupedView documents={documents} onDocumentClick={onDocumentClick} />
					)}
				</div>
			</div>
		</Modal>
	);
}

function DefaultView({
	documents,
	onDocumentClick,
}: {
	readonly documents: readonly MessageToolAnalysisDocument[];
	readonly onDocumentClick?: (doc: MessageToolAnalysisDocument) => void;
}) {
	return (
		<div className="flex flex-col">
			{documents.map((doc) => (
				<AnalysisDocumentRow
					key={doc.id}
					document={doc}
					onDocumentClick={onDocumentClick}
				/>
			))}
		</div>
	);
}

function GroupedView({
	documents,
	onDocumentClick,
}: {
	readonly documents: readonly MessageToolAnalysisDocument[];
	readonly onDocumentClick?: (doc: MessageToolAnalysisDocument) => void;
}) {
	const groups = groupAnalysisDocumentsBySourceLabel(documents);

	return (
		<div className="flex flex-col gap-3">
			{groups.map((group) => (
				<div key={group.key} className="flex flex-col gap-1.5">
					{group.label && <DocumentSectionHeader label={group.label} />}
					<div className="flex flex-col">
						{group.documents.map((doc) => (
							<AnalysisDocumentRow
								key={doc.id}
								document={doc}
								onDocumentClick={onDocumentClick}
							/>
						))}
					</div>
				</div>
			))}
		</div>
	);
}
